/**
 * Ability caches — where traversal abilities are found.
 *
 * docs/05 §8 makes "the first run of a profile is the Metroidvania" the load-
 * bearing claim of the whole design, and docs/02 §3 lists nine traversal
 * abilities as the progression spine. Neither was reachable: `grantAbility` had
 * no call site anywhere, so a run started with whatever the archetype and the
 * Attunement track supplied and ended with exactly that. Run one was a
 * Metroidvania with nothing to find.
 *
 * A cache is deliberately the altar's shape rather than a menu: a physical
 * thing on a plinth, claimed by walking to it and pressing interact. That is
 * the affordance the project already teaches in the first thirty seconds, and
 * docs/11 argues that reusing one strong interaction beats adding a second
 * weaker one.
 *
 * Unlike the altar it does not re-roll. An ability is not a gamble you wait
 * out — it is the thing the room was built around.
 */

import { Flag } from '../sim/materials';
import { MAT } from '../sim/materialData';
import type { CellWorld } from '../sim/world';

/** How close the player must be to claim. Matches the altar's reach. */
export const CACHE_RANGE = 22;

export interface AbilityCache {
  x: number;
  y: number;
  /** Traversal ability id, kebab-case — the vocabulary docs/02 §3 uses. */
  abilityId: string;
  claimed: boolean;
}

/**
 * The order abilities are handed out in.
 *
 * docs/02 §3.1 puts Breach early on the critical path and Phase Drift late,
 * and docs/02 §6 rule 5 says no ability may be dominated by one the player
 * already has — which is partly an ordering problem. Cling and Airstep come
 * first because they change ordinary traversal rather than opening a specific
 * door; Blink and Phase Drift come last because they trivialise the ones above
 * them if they arrive early.
 */
export const CACHE_ORDER: readonly string[] = [
  'airstep',
  'cling',
  'breach',
  'burrow',
  'tether',
  'fluid-adapt',
  'updraft',
  'blink',
  'phase-drift',
];

/** The next ability worth placing, or null when the player has them all. */
export function nextAbility(owned: Iterable<string>): string | null {
  const have = new Set(owned);
  for (const id of CACHE_ORDER) if (!have.has(id)) return id;
  return null;
}

/**
 * Carve the cache's chamber and pedestal.
 *
 * Built from real cells like the altar, and flagged STATIC for the same
 * reason: this is one of the few structures a run guarantees, and it should not
 * be eaten by the simulation before the player reaches it.
 */
export function buildCache(cells: CellWorld, c: AbilityCache): void {
  const bx = Math.round(c.x);
  const by = Math.round(c.y);

  for (let y = by - 20; y < by; y++) {
    for (let x = bx - 9; x <= bx + 9; x++) cells.set(x, y, 0);
  }
  // A narrow pedestal — read as a shrine, not as a floor.
  for (let y = by; y < by + 2; y++) {
    for (let x = bx - 3; x <= bx + 3; x++) {
      cells.set(x, y, y === by ? MAT.RUNESTONE : MAT.CERAMIC);
      cells.addFlag(x, y, Flag.STATIC);
    }
  }
  for (let y = by + 2; y < by + 10; y++) {
    for (let x = bx - 8; x <= bx + 8; x++) {
      cells.set(x, y, MAT.GRANITE);
      cells.addFlag(x, y, Flag.STATIC);
    }
  }
}

export function inRange(c: AbilityCache, px: number, py: number): boolean {
  const dx = c.x - px;
  const dy = c.y - py;
  return dx * dx + dy * dy < CACHE_RANGE * CACHE_RANGE;
}

/** Player-facing name and the one line that says what it opens — docs/02 §3. */
export const ABILITY_TEXT: Record<string, { name: string; opens: string }> = {
  airstep: { name: 'Airstep', opens: 'wide gaps, and ceilings three cells thick' },
  cling: { name: 'Cling', opens: 'vertical shafts' },
  tether: { name: 'Tether', opens: 'chasms and ceiling routes' },
  breach: { name: 'Breach', opens: 'cracked walls and brittle floors' },
  blink: { name: 'Blink', opens: 'thin sealed walls' },
  burrow: { name: 'Burrow', opens: 'sand seals and buried routes' },
  'fluid-adapt': { name: 'Fluid Adapt', opens: 'flooded regions' },
  'phase-drift': { name: 'Phase Drift', opens: 'sealed vaults' },
  updraft: { name: 'Updraft', opens: 'vertical ascents — you make the fire' },
};
